import { Col, Drawer, Row } from 'antd';
import { useState, useEffect, ChangeEvent, useContext } from 'react';
import ReactImageUploading from 'react-images-uploading';
import imgloading from '../../../assets/images/svg/image-loading-icon.svg';
import ShowImage from '../../../components/show_image';
import Spinner from '../../../components/spinner';
import FormControl from '../../../components/form_control';
import { Wrapper } from './upload_images_modal.styled';
import { Wrapper as PhotosModalWrapper } from '../../create_listings/screens/media/modals/add_photos_modal';
import { toast } from 'react-toastify';
import { S3ApiRepository } from '../../../data/repositories/s3_repository';
import { ImageType } from '../../../data/types/files';
import { FormValidators } from '../../../utils/functions/validation_functions';
import { Colors, Fonts } from '../../../config/theme';
import { MAXIMUM_IMAGE_FOR_LISTING } from '../../../pages/create_listings/constants';
import { Button, ButtonGroup, Spacer } from '@nextui-org/react';
import { TiDelete } from 'react-icons/ti';
import {
  FaCheck,
  FaCropSimple,
  FaMinus,
  FaPencil,
  FaPlus,
} from 'react-icons/fa6';
import {
  TbBackground,
  TbFreezeRow,
  TbRectangle,
  TbRectangleVertical,
} from 'react-icons/tb';
import UtilitiesRepository from '../../../data/repositories/utilities_repository';
import Lightbox from 'react-image-lightbox';
import { paddImage } from '../functions/image_library_functions';
import { useRef } from 'react';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { ArrowLeft, ArrowRight } from 'react-bootstrap-icons';
import { BsSquare } from 'react-icons/bs';
import {
  actionSetLastFetchedSilalImages,
  actionSetLastFetchedStoreImages,
} from '../../../store/images/image_library_actions';
import delete_image from '../../../assets/images/svg/delete-icon.svg';
import { Checkbox } from '@mui/material';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  StarOutlined,
  StarFilled,
} from '@ant-design/icons';
import { ListingState } from '../../../data/types/listings';
import { useDispatch } from 'react-redux';
import { actionSetListingData } from '../../../pages/create_listings/store/create_listing_actions';
import { PopupWithConfirmation } from '../../../components/popups';
import { ProductListingContext } from '../../../pages/create_listings/functions/data_formatting_functions';

export const MIN_IMAGE_SIZE = 10 * 1024; // 10KB
export const MIN_IMAGE_WIDTH = 500;
export const MIN_IMAGE_HEIGHT = 500;

type UploadImagesModalProps = {
  getData: () => void;
  store_id?: string;
  is_private?: boolean;
  filesRepository: any;
  s3Repository: S3ApiRepository;
  maxImages?: number;
  variant?:
    | 'faded'
    | 'flat'
    | 'solid'
    | 'bordered'
    | 'light'
    | 'shadow'
    | 'ghost'
    | undefined;
  postSuccessfullUploadCallback?: ({
    imagesLinks,
    imagesFiles,
    imagesObjects,
  }: {
    imagesLinks: string[];
    imagesFiles: { file: File }[];
    imagesObjects: ImageType[];
  }) => void;
  postAddAltsCallback?: () => void;
};

export const checkDimensions = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const isValid =
        img.width >= MIN_IMAGE_WIDTH && img.height >= MIN_IMAGE_HEIGHT;
      if (!isValid) {
        toast.warn(
          `Some images were too small or did not meet the dimension requirements. Minimum size is ${MIN_IMAGE_SIZE / 1024}KB, minimum width is ${MIN_IMAGE_WIDTH}px, and minimum height is ${MIN_IMAGE_HEIGHT}px. The given image has a width of ${img.width}px and a height of ${img.height}px and size of ${file.size / 1024}KB.
          The images that did not meet the requirements have been removed, please upload other images.`,
          {
            autoClose: 10000,
          },
        );
      }
      URL.revokeObjectURL(objectUrl); // Clean up memory
      resolve(isValid);
    };

    img.src = objectUrl;
  });
};

export function UploadImagesModal({
  getData,
  store_id,
  is_private,
  filesRepository,
  s3Repository,
  maxImages = 100,
  variant = 'flat',
  postSuccessfullUploadCallback,
  postAddAltsCallback,
}: UploadImagesModalProps) {
  const [childrenDrawer, setChildrenDrawer] = useState(false);
  const [imagesList, setImagesList] = useState<
    {
      file: File;
    }[]
  >([]);
  const [images, setImages] = useState<string[]>([]);
  const [imagesNumber, setImagesNumber] = useState<number>(
    MAXIMUM_IMAGE_FOR_LISTING,
  );
  const [image_url, setImageUrl] = useState('');
  const [selected_image, setSelectedImage] = useState<ImageType>();
  const [selected_index, setSelectedIndex] = useState(0);
  const [uploadedImages, setUploadedImages] = useState<ImageType[]>([]);
  const [modalchange, setModalchange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setAlt] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [cropper, setCropper] = useState<Cropper>();
  const imgRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<HTMLDivElement>(null);
  const [croppingIndex, setCroppingIndex] = useState(-1);

  const [index, setIndex] = useState(-1);
  const image = images?.[index];
  const currentImage = images?.[index];
  const nextIndex = (index + 1) % images?.length;
  const nextImage = images?.[nextIndex] || currentImage;
  const prevIndex = (index + images?.length - 1) % images?.length;
  const prevImage = images?.[prevIndex] || currentImage;
  const handleClick = (index: number) => setIndex(index);
  const handleClose = () => {
    setIndex(-1);
  };
  const handleMovePrev = () => setIndex(prevIndex);
  const handleMoveNext = () => setIndex(nextIndex);

  useEffect(() => {
    setUploadedImages([]);
    setImagesList([]);
    setImages([]);
  }, [isModalVisible]);

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalchange(false);
    setImagesNumber(MAXIMUM_IMAGE_FOR_LISTING);
  };

  const startCrop = () => {
    if (cropper) {
      cropper.destroy(); // Cleanup previous instance
      setCropper(undefined);
    }

    if (imgRef.current) {
      const cropperInstance = new Cropper(imgRef.current, {
        aspectRatio: 0,
        viewMode: 1,
        autoCropArea: 1,
        movable: true,
        zoomable: true,
        scalable: true,
        minContainerWidth: 1000,
        minContainerHeight: 700,
      });

      setCropper(cropperInstance);
    }
  };

  const applyCrop = (index: number) => {
    if (cropper) {
      const croppedCanvas = cropper.getCroppedCanvas();
      if (!croppedCanvas) {
        return;
      }
      // Get the cropped image as a Data URL
      const croppedImageUrl = croppedCanvas.toDataURL();
      // Update the image URL in the images array
      const newImages = images.map((img, i) =>
        i === index ? croppedImageUrl : img,
      );
      setImages(newImages);

      // Extract the base64 data from the data URL
      const base64Data = croppedImageUrl.split(',')[1];

      // Convert the base64 data to a Blob
      const croppedBlob = new Blob(
        [Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))],
        { type: 'image/png' },
      );

      // Create a new File with the cropped image and a proper file name
      const fileName = `cropped-image-${index}.png`;
      const newFile = new File([croppedBlob], fileName, { type: 'image/png' });

      // Update the imagesList with the new File
      const newImagesList = imagesList.map((img, i) =>
        i === index ? { file: newFile } : img,
      );
      setImagesList(newImagesList);

      // Clean up the cropper instance
      cropper.destroy();
      setCropper(undefined);
    }
  };

  const onChange = async (imageList: any) => {
    const validImages: any[] = [];

    // Filter images based on size and dimensions
    for (const image of imageList) {
      if (
        image.file.size >= MIN_IMAGE_SIZE &&
        (await checkDimensions(image.file))
      ) {
        validImages.push(image);
      } else {
        toast.warn(
          `Image ${image.file.name} was too small or did not meet the dimension requirements. Minimum size is ${MIN_IMAGE_SIZE / 1024}KB, minimum width is ${MIN_IMAGE_WIDTH}px, and minimum height is ${MIN_IMAGE_HEIGHT}px.`,
        );
      }
    }

    setImagesList(validImages);

    if (validImages.length > imagesNumber) {
      setImagesNumber(validImages.length);
    }

    setImages(validImages.map((i: any) => URL.createObjectURL(i.file), 'Img'));
  };

  const onChangeAlt = async (value: string) => {
    if (selected_image === undefined) return;
    uploadedImages.map((img) => {
      if (img.id === selected_image?.id) {
        img.alt = value;
      }
      return null;
    });
    setSelectedImage({ ...selected_image!, alt: value });
    setSelectedIndex(selected_index);
  };

  const uploadeImages = async () => {
    if (imagesList.length > 0) {
      setLoading(true);
      const successfulImages = await toast.promise(
        s3Repository.addImages({
          images: imagesList,
          is_public: !is_private,
        }),
        {
          pending: 'Uploading images...',
          success: 'Images uploaded successfully!',
          error: 'Error uploading images!',
        },
      );
      setLoading(false);
      if (!successfulImages) return;
      const imgs: ImageType[] = [];
      successfulImages.map((img: any) => {
        if (img.error) {
          toast.warning(img.reason);
          return null;
        }
        imgs.push(img.data.data.file);
        return null;
      });
      setUploadedImages(uploadedImages.concat(imgs));
      postSuccessfullUploadCallback &&
        postSuccessfullUploadCallback({
          imagesLinks: images,
          imagesFiles: imagesList,
          imagesObjects: imgs,
        });
      setSelectedImage(imgs[0]);
      setSelectedIndex(0);
      setModalchange(true);
      getData();
      if (store_id) {
        actionSetLastFetchedStoreImages(0);
      } else {
        actionSetLastFetchedSilalImages(0);
      }
    } else {
      toast.warn('Upload at least one image first!');
    }
  };

  useEffect(() => {
    if (uploadedImages.length > 0) {
      setSelectedImage(uploadedImages[0]);
      setSelectedIndex(0);
    }
  }, [uploadedImages]);

  const addAlts = async () => {
    setLoading(true);
    const res = await filesRepository.addAltsToStoreImages({
      images: uploadedImages,
      store_id: store_id,
    });
    setLoading(false);
    if (!res) return;
    setIsModalVisible(false);
    getData();
    postAddAltsCallback && postAddAltsCallback();
    setModalchange(false);
    setImagesList([]);
    setUploadedImages([]);
    setImages([]);
  };

  useEffect(() => {
    if (selected_image?.id) {
      setAlt(selected_image?.alt || selected_image?.original_name || '');
    }
  }, [selected_image]);

  const addImageFromUrl = async () => {
    if (!FormValidators.isURL(image_url)) {
      toast.warning('Please enter a valid image url!');
      return;
    }
    const image: {
      error: boolean;
      file?: File | undefined;
    } = await s3Repository.blobUrlToFile(image_url);
    setImageUrl('');
    if (!image.error && image.file) {
      setImages([...images, URL.createObjectURL(image.file)]);
      setImagesList([...imagesList, { file: image.file }]);
      setSelectedImage(uploadedImages[0]);
      setSelectedIndex(0);
    } else {
      toast.warn('Something went wrong! maybe url is not correct!');
    }
  };

  function createImageArray() {
    const imageArray = [];

    for (let i = 1; i <= imagesNumber; i++) {
      imageArray.push({ id: i });
    }

    return imageArray;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        // Right arrow key pressed
        // Do something for right arrow key
        if (selected_index + 1 < uploadedImages.length) {
          setSelectedImage(uploadedImages[selected_index + 1]);
          setSelectedIndex(selected_index + 1);
        }
      } else if (event.key === 'ArrowLeft') {
        // Left arrow key pressed
        // Do something for left arrow key
        if (selected_index - 1 >= 0) {
          setSelectedImage(uploadedImages[selected_index - 1]);
          setSelectedIndex(selected_index - 1);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selected_index, uploadedImages, selected_image, uploadedImages.length]);

  function parseBlobAndUpdateImages(blob: Blob | undefined, index: number) {
    if (blob) {
      // Update the image link with the new one
      const url = URL.createObjectURL(blob);
      const newImages = images.map((img, i) => (i === index ? url : img));
      setImages(newImages);
      const newImagesList = imagesList.map((img, i) =>
        i === index
          ? {
              data_url: url,
              file: new File([blob], 'updated.png', {
                type: 'image/png',
              }),
            }
          : img,
      );
      setImagesList(newImagesList);
    }
    setLoading(false);
  }

  return (
    <>
      {image && (
        <Lightbox
          mainSrc={image}
          mainSrcThumbnail={image}
          nextSrc={nextImage}
          prevSrc={prevImage}
          onCloseRequest={handleClose}
          onMovePrevRequest={handleMovePrev}
          onMoveNextRequest={handleMoveNext}
        />
      )}
      <Button
        onClick={() => setIsModalVisible(true)}
        variant={variant}
        className="ml-3 "
        color="success"
      >
        Upload +
      </Button>
      <Drawer
        zIndex={1000}
        open={isModalVisible}
        title={
          !modalchange ? (
            <div className="heading">
              <h4>
                {store_id
                  ? 'Upload New Photos to Store Library'
                  : 'Upload New Photos to Silal Library'}
              </h4>
              <span>
                After uploading images, click on the{' '}
                <span
                  style={{
                    color: Colors.greenMain,
                    fontWeight: 600,
                  }}
                >
                  Pencil
                </span>{' '}
                icon to: crop, remove background or add padding to it. And the{' '}
                <span
                  style={{
                    color: Colors.tomatoRed,
                    fontWeight: 600,
                  }}
                >
                  Trash
                </span>{' '}
                icon to delete it.
              </span>
              <p>You can upload up to {maxImages} at a time.</p>
            </div>
          ) : (
            <>
              <h4 className="heading">Add alts for pictures</h4>
              <p className="desc">
                Adding an alt to your pictures significantly help customers to
                find them.
                <br />
                <span
                  style={{
                    color: Colors.greenMain,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  Hint:{' '}
                </span>
                You can use Left & Right arrows for quicker navigation{' '}
              </p>
            </>
          )
        }
        width={'75%'}
        extra={
          !modalchange ? (
            <Button
              key="loading"
              color={loading || imagesList?.length < 1 ? 'default' : 'success'}
              style={{
                width: '150px',
              }}
              isDisabled={loading}
              onClick={() => uploadeImages()}
            >
              Upload
            </Button>
          ) : (
            <Button
              style={{
                width: '150px',
                position: 'absolute',
                right: '20px',
                top: '50px',
              }}
              color={'success'}
              onClick={addAlts}
            >
              Add Alts
            </Button>
          )
        }
        footer={
          modalchange ? (
            <></>
          ) : (
            <div // <Icon
              key={'just'}
              className="w-100 d-flex justify-content-between"
              style={{ float: 'left' }}
            >
              <FormControl
                value={image_url}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setImageUrl(e.target.value)
                }
                input={true}
                placeholder="Image URL"
                style={{
                  position: 'relative',
                  marginRight: '10px',
                  marginBottom: '20px',
                  width: '90%',
                }}
              />
              <Button
                style={{
                  width: '100px',
                  marginLeft: '10px',
                  marginTop: '10px',
                  color: 'white',
                  backgroundColor: image_url === '' ? 'gray' : Colors.greenMain,
                  cursor: image_url === '' ? 'not-allowed' : 'pointer',
                }}
                onClick={image_url === '' ? () => {} : addImageFromUrl}
              >
                Add
              </Button>
            </div>
          )
        }
        onClose={
          uploadedImages.length < 1
            ? () => {
                handleCancel();
              }
            : () => {
                toast.warning('Please add alts to images before closing');
              }
        }
      >
        <Wrapper>
          {/* <CurrentListingImages
            selection={false}
            addOrSelectActionsOnly={false}
          /> */}
          {!modalchange ? (
            <>
              <ReactImageUploading
                multiple
                value={imagesList}
                onChange={onChange}
                maxNumber={maxImages}
                dataURLKey="data_url"
              >
                {({ onImageUpload, isDragging, dragProps }: any) => (
                  <div className="contain">
                    {' '}
                    <br />
                    <div // <Icon
                      className="section text-center"
                      style={
                        isDragging
                          ? {
                              color: Colors.greenMain,
                              border: `2px solid ${Colors.greenMain}`,
                            }
                          : { cursor: 'pointer' }
                      }
                      onClick={onImageUpload}
                      key="section"
                      {...dragProps}
                    >
                      <img
                        src={imgloading}
                        alt="..."
                        style={{
                          margin: 'auto',
                        }}
                      />
                      <h5>Click or Drag and drop images to upload files</h5>
                      <h6>Supported formats: PNG, JPG, JPEG, SVG, WEBP, BMP</h6>

                      <div className="grid-container">
                        {createImageArray().map((_, index) => {
                          return (
                            <div // <Icon
                              key={index}
                            >
                              {images[index] ? (
                                <div
                                  className="media_image"
                                  key={index}
                                  style={{ position: 'relative' }}
                                >
                                  <img
                                    key={index}
                                    src={images[index]}
                                    alt={'image123'}
                                    style={{
                                      padding: '4px',
                                      border: `1px solid ${Colors.greenMain}`,
                                      maxHeight: '200px',
                                      maxWidth: '100%',
                                      minWidth: '100%',
                                      minHeight: '200px',
                                      objectFit: 'contain',
                                      borderRadius: '5px',
                                    }}
                                    onClick={(
                                      event: React.MouseEvent<HTMLImageElement>,
                                    ) => {
                                      event.stopPropagation();
                                      handleClick(index);
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '10px',
                                      left: '10px',
                                      display: 'flex',
                                      gap: '10px',
                                    }}
                                  >
                                    <Button
                                      isIconOnly
                                      color="danger"
                                      onClick={() => {
                                        // Remove image from the list
                                        const newImages = images.filter(
                                          (_, i) => i !== index,
                                        );
                                        const newImagesList = imagesList.filter(
                                          (_, i) => i !== index,
                                        );
                                        setImages(newImages);
                                        setImagesList(newImagesList);
                                      }}
                                    >
                                      <TiDelete />
                                    </Button>
                                  </div>
                                  <div
                                    style={{
                                      position: 'absolute',
                                      bottom: '10px',
                                      right: '10px',
                                      display: 'flex',
                                      gap: '10px',
                                    }}
                                  >
                                    <Button
                                      isIconOnly
                                      color="success"
                                      onClick={() => {
                                        setCroppingIndex(index);
                                        setChildrenDrawer(true);
                                      }}
                                    >
                                      <FaPencil />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div // <Icon
                                  style={{
                                    padding: '4px',
                                    maxHeight: '200px',
                                    maxWidth: '100%',
                                    minWidth: '100%',
                                    minHeight: '200px',
                                    objectFit: 'contain',
                                    borderRadius: '5px',
                                  }}
                                  key={index}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </ReactImageUploading>
            </>
          ) : (
            <>
              <div
                className="d-flex justify-content-center
               align-items-center
              "
              >
                <button
                  onClick={() => {
                    if (selected_index - 1 >= 0) {
                      setSelectedImage(uploadedImages[selected_index - 1]);
                      setSelectedIndex(selected_index - 1);
                    }
                  }}
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: Colors.greenMain,
                    color: 'white',
                    borderRadius: '20%',
                    fontSize: '30px',
                    border: `solid 1px ${Colors.greenMain}`,
                  }}
                >
                  {'<'}
                </button>
                <div className="image-box d-flex justify-content-center">
                  <ShowImage
                    image={selected_image!}
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    if (selected_index + 1 < uploadedImages.length) {
                      setSelectedImage(uploadedImages[selected_index + 1]);
                      setSelectedIndex(selected_index + 1);
                    }
                  }}
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: Colors.greenMain,
                    color: 'white',
                    fontSize: '30px',
                    borderRadius: '20%',
                    border: `solid 1px ${Colors.greenMain}`,
                  }}
                >
                  {'>'}
                </button>
              </div>
              {loading ? (
                <div className="justify-content-center text-center">
                  <div // <Icon
                    style={{
                      position: 'absolute',
                      right: '30px',
                      bottom: '30px',
                    }}
                  >
                    <Spinner />
                  </div>
                  <h5>Adding Alts to images...</h5>
                </div>
              ) : (
                <>
                  <div className="row d-flex">
                    <FormControl
                      input={true}
                      type="text"
                      placeholder="image alt"
                      value={
                        selected_image?.alt !== undefined &&
                        selected_image?.alt !== null
                          ? selected_image?.alt
                          : selected_image?.original_name || ''
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onChangeAlt(e.target.value)
                      }
                    />
                  </div>
                  <div className="my-4">
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {uploadedImages?.map((image, index) => (
                        <div key={index} style={{ padding: '8px' }}>
                          <button
                            className={`alt-images ${
                              image.id === selected_image?.id ? 'active' : ''
                            }`}
                            style={{
                              width: '200px',
                              height: '200px',
                            }}
                            onClick={() => {
                              setSelectedImage(image);
                              setSelectedIndex(index);
                            }}
                          >
                            <ShowImage
                              image={image}
                              style={{
                                width: '100%',
                                height: '100%',
                              }}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          <Drawer
            width={'60%'}
            closable={false}
            onClose={() => {
              setChildrenDrawer(false);
              setLoading(false);
            }}
            open={childrenDrawer}
            title={
              <div className="flex flex-col">
                <Row className="justify-between">
                  <h4>Edit Image </h4>
                  <Row>
                    <Button
                      startContent={<ArrowLeft />}
                      color="success"
                      variant="bordered"
                      isDisabled={croppingIndex - 1 < 0}
                      style={{
                        marginRight: '10px',
                      }}
                      onClick={() => {
                        setCroppingIndex(
                          croppingIndex - 1 < 0 ? 0 : croppingIndex - 1,
                        );
                      }}
                    >
                      Prev Image
                    </Button>

                    <Button
                      endContent={<ArrowRight />}
                      color="success"
                      variant="bordered"
                      isDisabled={croppingIndex + 1 >= images.length}
                      onClick={() => {
                        setCroppingIndex(
                          croppingIndex + 1 >= images.length
                            ? images.length - 1
                            : croppingIndex + 1,
                        );
                      }}
                    >
                      Next Image
                    </Button>
                    <Button
                      isIconOnly
                      style={{
                        color: 'white',
                        backgroundColor: Colors.greenMain,
                        marginLeft: '20px',
                      }}
                      onClick={() => {
                        applyCrop(croppingIndex);
                        setChildrenDrawer(false);
                      }}
                    >
                      <FaCheck />
                    </Button>
                  </Row>
                </Row>
                <div className=" flex  justify-center m-2 mt-4">
                  <Button
                    isDisabled={loading}
                    style={{
                      color: 'white',
                      backgroundColor: Colors.blue,
                    }}
                    onClick={async () => {
                      setLoading(true);
                      const blob = await UtilitiesRepository.removeImageBg({
                        file: imagesList[croppingIndex].file,
                      });
                      if (!blob) {
                        setLoading(false);
                        return;
                      }
                      parseBlobAndUpdateImages(blob, croppingIndex);
                    }}
                    endContent={<TbBackground />}
                  >
                    Remove Background
                  </Button>
                  <Spacer x={2} />
                  <Button
                    style={{
                      color: 'white',
                      backgroundColor: Colors.purple,
                    }}
                    onClick={async () => {
                      const blob = await paddImage(images[croppingIndex], 50);
                      parseBlobAndUpdateImages(blob, croppingIndex);
                    }}
                    endContent={<FaPlus />}
                  >
                    Add Margin
                  </Button>{' '}
                  <Spacer x={2} />
                  <Button
                    color="warning"
                    style={{
                      color: 'white',
                    }}
                    onClick={() => {
                      applyCrop(croppingIndex);
                    }}
                    endContent={<FaCropSimple />}
                  >
                    Apply Crop
                  </Button>
                </div>
                <Spacer y={2} />
                <div className="flex flex-col items-center">
                  <span
                    style={{
                      fontSize: '14px',
                      color: Colors.alternateText,
                      fontFamily: Fonts.secondary,
                      textAlign: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    You can use the buttons below to set the aspect ratio of the
                    crop frame
                  </span>
                  <Col className="justify-center">
                    <ButtonGroup>
                      <Button
                        color="warning"
                        variant="flat"
                        onClick={() => {
                          cropper?.setAspectRatio(0);
                        }}
                        startContent={<TbFreezeRow />}
                      >
                        Free Crop
                      </Button>
                      <Button
                        color="warning"
                        variant="flat"
                        onClick={() => {
                          cropper?.setAspectRatio(1.5 / 1);
                        }}
                        startContent={<TbRectangle />}
                      >
                        1.5:1
                      </Button>
                      <Button
                        color="warning"
                        variant="flat"
                        onClick={() => {
                          cropper?.setAspectRatio(9 / 16);
                        }}
                        startContent={<TbRectangleVertical />}
                      >
                        Vertical Rectangle
                      </Button>
                      <Button
                        color="warning"
                        variant="flat"
                        onClick={() => {
                          cropper?.setAspectRatio(1 / 1);
                        }}
                        startContent={<BsSquare />}
                      >
                        Square
                      </Button>
                    </ButtonGroup>
                  </Col>
                </div>
              </div>
            }
          >
            <div // <Icon
              key={croppingIndex}
              ref={cropperRef}
              style={{
                width: '100%',
                height: '100%',
                minHeight: '100%',
                minWidth: '100%',
              }}
            >
              <img
                key={croppingIndex}
                ref={imgRef}
                src={images[croppingIndex]}
                alt={'image123'}
                style={{
                  padding: '4px',
                  border: `1px solid ${Colors.greenMain}`,
                  borderRadius: '5px',
                  width: '100%',
                  height: '100%',
                  minHeight: '100%',
                  minWidth: '100%',
                }}
                onLoad={() => startCrop()}
                onClick={() => handleClick(croppingIndex)}
              />
            </div>
          </Drawer>
        </Wrapper>
      </Drawer>
    </>
  );
}
